import { appApiUrl } from 'config'
import {
  API_AI_ASSISTANT_CONVERSATIONS,
  API_AI_ASSISTANT_HISTORY,
  API_AI_ASSISTANT_MODEL,
  API_AI_ASSISTANT_PROMPT,
  API_AI_ASSISTANT_PROMPT_STOP,
  API_AI_ASSISTANT_PROMPT_STREAM,
  API_AI_ASSISTANT_UPLOAD,
  API_AI_PROMPT,
} from 'config/api.url.constants'
import { BaseFile } from 'config/index'
import { store } from 'reducers'
import Api from 'services/api'

import type { AiConversation } from './aiProjects'

/**
 * One step of a turn, as the transcript shows it.
 *
 * `label` is the phrase the live status line has always shown. Everything else is what Claude's own
 * tool cards show: which tool ran, what it was asked for, and what came back. Steps persisted before
 * the cards landed are bare strings, so every reader handles `string | AssistantStep`.
 */
/** What a model call cost. Only on the model steps; tools and older turns carry none. */
export interface AssistantStepUsage {
  input?: number
  output?: number
  /** Input tokens served from the cache instead of being re-read. */
  cacheRead?: number
  cacheWrite?: number
  thinking?: number
}

export interface AssistantStep {
  label: string
  kind: 'thinking' | 'tool' | 'web_search' | 'answer' | 'error'
  tool?: string
  input?: Record<string, any>
  result?: string
  ok?: boolean
  ms?: number
  usage?: AssistantStepUsage
}

export interface AssistantMessage {
  id: string
  role: 'user' | 'model'
  content: string
  createdAt: string
  files?: AssistantPromptFile[]
  /** Ordered "what it did" steps captured from the live status stream (current turn only). */
  steps?: Array<string | AssistantStep>
  /**
   * The Messages-API messages the turn produced - its tool calls and their results - handed back so
   * the NEXT turn can replay what was actually looked up instead of only the prose that came out of
   * it. Opaque to the UI: it is carried, never read.
   */
  blocks?: any[]
}

export interface AssistantPromptFile {
  fileKey: string
  name: string
}

/**
 * How the assistant fills in specifics when it drafts a note or email:
 * 'values' substitutes real loan data (the default), 'placeholders' leaves a reusable
 * template like [Borrower Name]. A clear instruction in the message overrides either.
 */
export type AssistantDraftMode = 'values' | 'placeholders'

export interface AssistantPromptRequest {
  prompt: string
  history?: AssistantMessage[]
  context?: string
  promptFiles?: AssistantPromptFile[]
  draftMode?: AssistantDraftMode
  /** Project whose instructions, knowledge, and artifacts this turn should run against. */
  projectId?: number
  /** Persisted thread; when set, the backend saves both sides of the turn. */
  conversationId?: number
  /**
   * The loan the user is working in. Loan-scoped tools default to it, so the assistant answers
   * about the open loan without being told which one. Sent explicitly rather than relying on the
   * loan-number header, which keeps its last value after you leave a loan.
   */
  loanNumber?: number
  /**
   * An id for this turn, so it can be stopped.
   *
   * Stopping is an explicit act: Stop posts this id to the stop endpoint. Losing the connection - a
   * reload, a closed tab - is not a stop, and the turn finishes and is saved to the thread.
   */
  turnId?: string
  /**
   * The turn arrived by voice and the answer will be read aloud, so it is asked for in a shape
   * worth hearing: a couple of sentences, no markdown, no tables.
   */
  voice?: boolean
}

/** An artifact the assistant created or revised while answering, so the UI can open it. */
export interface AssistantArtifactRef {
  id: number
  title: string
  kind: string
  version: number
  action: 'created' | 'updated'
}

export interface AssistantPromptResponse {
  data: AssistantMessage | string
  artifacts?: AssistantArtifactRef[]
  /** Ordered "what it did" steps for this turn (server-side source of truth). */
  steps?: Array<string | AssistantStep>
  /** The turn's tool-call messages, to be sent back with the next prompt. See AssistantMessage. */
  blocks?: any[]
  /**
   * The model that actually answered this turn. The assistant falls back between paths (native
   * Claude, the shared AI service, documents only), so this can differ from what
   * getAssistantModel() predicted before the turn ran.
   */
  model?: string
}

export interface AssistantHistoryResponse {
  data: AssistantMessage[]
}

export const postAiPrompt = (
  identifier: string,
  type: string,
  data: {
    files: BaseFile[]
    baseFiles: BaseFile[]
    history: Record<string, any>[]
    options?: Record<string, any>
    [key: string]: any
  },
) => {
  return Api.post(API_AI_PROMPT, data, { identifier, type }, { timeout: 0 })
}

export const postAssistantPrompt = (data: AssistantPromptRequest): Promise<AssistantPromptResponse> => {
  return Api.post(API_AI_ASSISTANT_PROMPT, data, {}, { timeout: 0 })
}

/**
 * A fresh id for a turn, so it can be named to the stop endpoint later.
 *
 * `crypto.randomUUID` is missing in older browsers and on any page not served over https, so the
 * fallback is not theoretical - and an id that never arrives would quietly make Stop do nothing.
 */
export const newAssistantTurnId = (): string => {
  const uuid = (globalThis.crypto as any)?.randomUUID?.()
  return uuid || `turn-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Stop a turn that is still running, by the id it was sent with.
 *
 * Aborting the request alone is not enough: the server keeps a turn going when the connection drops
 * (that is what makes a reload harmless), so a deliberate Stop has to say so. Failures are ignored -
 * the turn may have finished a moment ago, and either way the caller is about to abort its own
 * request and stop showing it.
 */
export const stopAssistantPrompt = (turnId: string): Promise<any> => {
  return Api.post(API_AI_ASSISTANT_PROMPT_STOP, { turnId }, {}, { timeout: 0 }).catch(() => null)
}

// Stream the assistant response as Server-Sent Events. onStep fires per step with the whole card
// (tool, arguments, result) and onStatus with just its phrase, so a caller that only wants the live
// line does not have to know about steps. The promise resolves with the final envelope (same shape
// postAssistantPrompt returns). Throws on transport/abort/error, so callers keep their error branch.
export const streamAssistantPrompt = async (
  data: AssistantPromptRequest,
  handlers: {
    onStatus?: (text: string) => void
    onStep?: (step: AssistantStep) => void
    /**
     * The answer as the model writes it. `reset` starts the text over, which happens whenever the
     * agent begins a new step: the prose before a tool call is narration, not the answer.
     *
     * Only sent when the backend's model transport can stream. When it cannot, no deltas arrive and
     * the turn still ends with the same `done` payload, so this is additive either way.
     */
    onDelta?: (delta: { text?: string; reset?: boolean }) => void
    signal?: AbortSignal
  } = {},
): Promise<AssistantPromptResponse> => {
  const token = store.getState()?.auth?.token
  const res = await fetch(`${appApiUrl}${API_AI_ASSISTANT_PROMPT_STREAM}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` },
    body: JSON.stringify(data),
    signal: handlers.signal,
  })
  if (!res.ok || !res.body) throw new Error(`Assistant stream failed (${res.status})`)

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let result: AssistantPromptResponse = { data: '' }

  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    // SSE events are separated by a blank line; a `data:` line carries the JSON payload.
    let sep = buffer.indexOf('\n\n')
    while (sep !== -1) {
      const raw = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      const dataLine = raw.split('\n').find((line) => line.startsWith('data:'))
      if (dataLine) {
        let event: any = null
        try {
          event = JSON.parse(dataLine.slice(5).trim())
        } catch {
          event = null // a malformed data line is skipped, not fatal
        }
        if (event?.type === 'status') {
          handlers.onStatus?.(String(event.text || ''))
          // `step` is only sent by a backend new enough to have it; the two repos deploy
          // separately, so the phrase above stays the guaranteed part of the contract.
          if (event.step?.label) handlers.onStep?.(event.step as AssistantStep)
        }
        else if (event?.type === 'delta') handlers.onDelta?.({ text: event.text, reset: event.reset })
        else if (event?.type === 'done') result = event.data
        else if (event?.type === 'error') throw new Error(event.message || 'Assistant error')
      }
      sep = buffer.indexOf('\n\n')
    }
  }
  return result
}

export const getAssistantHistory = (): Promise<AssistantHistoryResponse> => {
  return Api.get(API_AI_ASSISTANT_HISTORY)
}

export interface AssistantModelInfo {
  engine: string
  model: string
}

/**
 * The model the assistant is expected to answer with, shown in the panel header until a turn
 * reports the one that actually answered.
 *
 * The endpoint returns the pair as the whole body, and Api.get resolves to that body - so reading
 * `res.data.model` (as this used to) was always undefined and the badge never rendered. The `data`
 * branch stays for a backend that wraps it, since the two repos deploy separately.
 */
export const getAssistantModel = async (): Promise<AssistantModelInfo> => {
  const res: any = await Api.get(API_AI_ASSISTANT_MODEL)
  const info = res?.engine || res?.model ? res : res?.data
  return { engine: info?.engine || '', model: info?.model || '' }
}

export const getAssistantConversations = (loanNumber?: number): Promise<AiConversation[]> =>
  Api.get(API_AI_ASSISTANT_CONVERSATIONS, loanNumber ? { loanNumber } : {})

export const createAssistantConversation = (loanNumber?: number, title?: string): Promise<AiConversation> =>
  Api.post(API_AI_ASSISTANT_CONVERSATIONS, { title, loanNumber: loanNumber || undefined })

export interface AssistantUploadResponse {
  data: AssistantPromptFile[]
}

export const postAssistantUpload = (files: File[]): Promise<AssistantUploadResponse> => {
  return Api.uploadFiles(API_AI_ASSISTANT_UPLOAD, {}, {}, files, { timeout: 0 })
}
